import type { DragEvent } from 'react'
import type { CollectionBlock } from '../../lib/types'
import { collectionPageUrl, safeCollection } from '../../lib/urls'
import { CollectionCardBody, collectionCardCls } from '../CollectionLinkCard'
import { DragGrip } from './BlockViews'
import type { SelectableProps } from './ThoughtParts'
import { BlockToolbar, RemoveButton, ToolbarButton, ToolbarLabel } from './Toolbar'

/**
 * A collection card in the composer: the reader's card, selectable like a
 * link card, with Open, Change and Remove. A private collection says readers
 * won't see it. What it shows was snapshotted when picked; Change picks again
 * (fresh count, or a collection made public since).
 */
export function CollectionBlockView({
  block,
  selected,
  onSelect,
  onKeyDown,
  onRemove,
  onChange,
  onDragStart,
}: SelectableProps & {
  block: CollectionBlock
  /** Pick another collection for this card. */
  onChange: () => void
  /** Its grip: drag to move it, or beside another block as a column. */
  onDragStart?: (e: DragEvent<HTMLElement>) => void
}) {
  const shown = safeCollection(block) ?? block
  const href = collectionPageUrl(shown.slug)
  return (
    <div
      data-selectable={block.id}
      tabIndex={0}
      role="group"
      aria-label={`Collection card: ${block.name}`}
      onClick={(e) => {
        e.stopPropagation()
        onSelect()
      }}
      onKeyDown={onKeyDown}
      className={`group/card relative mb-[26px] outline-none ${collectionCardCls} transition-shadow ${selected ? 'ring-2 ring-accent/40' : ''}`}
    >
      {onDragStart && <DragGrip selected={!!selected} onDragStart={onDragStart} />}
      {selected && (
        <BlockToolbar label="Collection card options">
          <ToolbarLabel>Collection</ToolbarLabel>
          {href && (
            <ToolbarButton accent onClick={() => window.open(href, '_blank', 'noopener,noreferrer')}>
              Open
            </ToolbarButton>
          )}
          <ToolbarButton onClick={onChange}>Change</ToolbarButton>
          <RemoveButton onClick={onRemove} />
        </BlockToolbar>
      )}
      <CollectionCardBody
        block={shown}
        explore={!!href}
        note={href ? null : <span className="text-accent"> · Private, so readers won’t see this card</span>}
      />
    </div>
  )
}
